import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FlashbarProps } from "@amzn/awsui-components-react/polaris";
import {
  getFromLocalStorage,
  writeToLocalStorage,
} from "../../util/localStorageUtils";
import logger from "../../art-logger-config";

interface CacheableNotificationState {
  notificationItems: Record<number, FlashbarProps.MessageDefinition>;
}

// Keys are epoch-ms timestamps and the render-time filter in
// `CacheableNotification` hides anything older than this window. The
// hydration path drops the same stale entries so a persisted item
// never flashes on mount, and Cloudscape never briefly reserves
// notification-slot height for something we'd immediately hide.
export const CACHEABLE_NOTIFICATION_TTL_MS = 30_000;

function isFreshEntry(rawKey: string, nowMs: number): boolean {
  const key = Number.parseInt(rawKey, 10);
  if (!Number.isFinite(key)) return false;
  return nowMs - key < CACHEABLE_NOTIFICATION_TTL_MS;
}

function pruneStaleEntries(
  items: Record<number, FlashbarProps.MessageDefinition>,
  nowMs: number
): Record<number, FlashbarProps.MessageDefinition> {
  const fresh: Record<number, FlashbarProps.MessageDefinition> = {};
  for (const [rawKey, value] of Object.entries(items)) {
    if (isFreshEntry(rawKey, nowMs)) {
      fresh[Number.parseInt(rawKey, 10)] = value;
    }
  }
  return fresh;
}

function getInitialPreferences(): CacheableNotificationState {
  const cacheableNotificationInitialState: CacheableNotificationState = {
    notificationItems: {},
  };

  try {
    const localPrefsString = getFromLocalStorage("NOTIFICATION_PREFERENCES");
    if (localPrefsString === null) {
      return cacheableNotificationInitialState;
    }
    const parsed = JSON.parse(localPrefsString) as CacheableNotificationState;
    const nowMs = Date.now();
    const freshItems = pruneStaleEntries(parsed.notificationItems ?? {}, nowMs);
    // If pruning changed the persisted set, rewrite storage so the
    // next mount doesn't re-do the work. `writeToLocalStorage`
    // handles storage errors internally, so no outer try/catch here.
    if (
      Object.keys(freshItems).length !==
      Object.keys(parsed.notificationItems ?? {}).length
    ) {
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: freshItems })
      );
    }
    return { notificationItems: freshItems };
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.error(
      `Failed to load initial preferences for cacheable notification: ${errorMessage}`
    );
  }
  return cacheableNotificationInitialState;
}

const cacheableNotification = createSlice({
  name: "cacheableNotification",
  initialState: getInitialPreferences(),
  reducers: {
    addCacheableNotification(
      state: CacheableNotificationState,
      action: PayloadAction<{
        key: number;
        message: FlashbarProps.MessageDefinition;
      }>
    ) {
      state.notificationItems[action.payload.key] = action.payload.message;
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
    removeCacheableNotification(
      state: CacheableNotificationState,
      action: PayloadAction<number>
    ) {
      delete state.notificationItems[action.payload];
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
    clearCacheableNotifications(state: CacheableNotificationState) {
      state.notificationItems = {};
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
  },
});

export const {
  addCacheableNotification,
  removeCacheableNotification,
  clearCacheableNotifications,
} = cacheableNotification.actions;

export default cacheableNotification.reducer;

export function createCacheableNotificationDefinition(
  type: FlashbarProps.Type,
  header: string,
  dismissible: boolean,
  key: number,
  content?: string
) {
  return addCacheableNotification({
    key: key,
    message: {
      type: type,
      header: header,
      dismissible: dismissible,
      content: content,
    },
  });
}
